import { useState, useEffect, useRef, useCallback } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { useBrand } from "@/config/brands";
import { supabase } from "@/integrations/supabase/client";
import { loadStaticData } from "@/lib/offlineData";
import { stateAbbreviations } from "@/components/USAMapNavigator";
import { cityMatches } from "@/lib/mapNav";
import { titleCaseHospital, titleCaseCity } from "@/lib/hospitalName";

export interface HospitalRankingsTableProps {
  onHospitalClick: (hospitalId: string) => void;
  selectedState: string;
  selectedCity: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  onStateSelect: (state: string) => void;
}

export const HospitalRankingsTable = ({
  onHospitalClick,
  selectedState,
  selectedCity,
  onStateChange,
  onCityChange,
  onStateSelect
}: HospitalRankingsTableProps) => {
  const { t } = useTranslation('map');
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const accentColor = isAhp ? "text-civic-gold" : "text-matrix-green";
  const [statePopoverOpen, setStatePopoverOpen] = useState(false);
  const [displayCount, setDisplayCount] = useState(30);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: rankings, isLoading } = useQuery({
    queryKey: ['hospital-rankings-map'],
    queryFn: async () => {
      // Try static data first (encoded .dat on Capacitor, .json on web)
      try {
        const staticData = await loadStaticData<{ data: any[] }>('rankings.json');
        if (staticData && Array.isArray(staticData.data) && staticData.data.length > 0) {
          return staticData.data.map((r: any) => ({
            hospital_id: r.hospital_id,
            hospital_name: r.hospital_name,
            hospital_city: r.hospital_city,
            hospital_state: r.hospital_state,
            avg_gross_markup_percentage: r.avg_gross_markup_percentage,
            median_gross_markup_percentage: r.median_gross_markup_percentage,
            total_procedures: r.total_procedures,
            outlier_expensive_count: r.outlier_expensive_count,
            overall_rank_gross: r.overall_rank_gross,
            // Quality + related info (already in rankings.json — joined from hospitals)
            cms_overall_rating: r.cms_overall_rating,
            google_rating: r.google_rating,
            google_review_count: r.google_review_count,
            bed_count: r.bed_count,
            hospital_type: r.hospital_type,
            emergency_services: r.emergency_services,
          }));
        }
      } catch {
        // Fall through to Supabase
      }

      // Fallback: fetch from Supabase
      const { data, error } = await supabase
        .from('hospital_ranking_summary')
        .select('hospital_id, hospital_name, city, state, avg_gross_markup_percentage, median_gross_markup_percentage, total_procedures, outlier_expensive_count, overall_rank_gross')
        .not('avg_gross_markup_percentage', 'is', null)
        .order('overall_rank_gross', { ascending: true });

      if (error) throw error;
      return (data || []).map((r: any) => ({
        hospital_id: r.hospital_id,
        hospital_name: r.hospital_name || t('rankings.unknownHospital'),
        hospital_city: r.city,
        hospital_state: r.state,
        avg_gross_markup_percentage: r.avg_gross_markup_percentage,
        median_gross_markup_percentage: r.median_gross_markup_percentage,
        total_procedures: r.total_procedures,
        outlier_expensive_count: r.outlier_expensive_count,
        overall_rank_gross: r.overall_rank_gross,
      }));
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  // Filter rankings by selected state and city
  let filteredRankings = rankings;

  if (selectedState !== 'USA') {
    // Case-insensitive so lowercase URL params ("/map/tx") still match.
    filteredRankings = filteredRankings?.filter(
      (r: any) => (r.hospital_state || '').toUpperCase() === selectedState.toUpperCase()
    );
  }

  if (selectedCity) {
    // Punctuation/case-insensitive so URL slugs ("winston-salem") match DB cities.
    filteredRankings = filteredRankings?.filter((r: any) => cityMatches(r.hospital_city, selectedCity));
  }

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(30);
  }, [selectedState, selectedCity]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !filteredRankings) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && displayCount < filteredRankings.length) {
      setDisplayCount(prev => Math.min(prev + 30, filteredRankings.length));
    }
  }, [displayCount, filteredRankings]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">{t('rankings.loading')}</div>
      </div>
    );
  }

  // Get state name from abbreviation
  const getStateName = (stateCode: string) => {
    if (stateCode === 'USA') return t('rankings.allHospitals');
    const entry = Object.entries(stateAbbreviations).find(([_, code]) => code === stateCode);
    return entry ? entry[0] : stateCode;
  };

  const getRowColor = (markup: number) => {
    if (markup > 1000) return 'bg-red-950/80 hover:bg-red-950';
    if (markup > 500) return 'bg-red-900/60 hover:bg-red-900/80';
    if (markup > 300) return 'bg-orange-900/40 hover:bg-orange-900/60';
    if (markup > 200) return 'bg-yellow-900/30 hover:bg-yellow-900/50';
    if (markup > 100) return 'bg-yellow-800/20 hover:bg-yellow-800/40';
    if (markup > 50) return 'bg-orange-800/20 hover:bg-orange-800/40';
    if (markup > 0) return 'bg-red-800/20 hover:bg-red-800/40';
    return 'bg-green-900/20 hover:bg-green-900/40'; // 0% or negative
  };

  const getMarkupColor = (markup: number) => {
    // Follow outlier legend: 40%+ red, 25-39% orange, 10-24% yellow, <10% green
    if (markup >= 40) return 'text-red-500';
    if (markup >= 25) return 'text-orange-500';
    if (markup >= 10) return 'text-yellow-500';
    return 'text-green-500';
  };

  // Rating → text color (CMS 1–5 stars, Google 0–5). Higher = better = green.
  const ratingColor = (r: number | null | undefined) => {
    if (r == null) return isAhp ? 'text-white/40' : 'text-muted-foreground';
    if (r >= 4) return 'text-green-500';
    if (r >= 3) return 'text-yellow-500';
    return 'text-red-500';
  };

  // Compact "type · N beds · ER" meta line under the hospital name.
  const hospitalMeta = (r: any): string => {
    const parts: string[] = [];
    if (r.hospital_type) parts.push(String(r.hospital_type).replace(/ Hospitals?$/i, ''));
    if (r.bed_count) parts.push(t('rankings.beds', { n: Number(r.bed_count).toLocaleString() }));
    if (r.emergency_services) parts.push(t('rankings.er'));
    return parts.join(' · ');
  };

  // Compact review count: 1234 → 1.2k
  const fmtReviews = (n: number | null | undefined) => {
    if (!n) return '';
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
  };

  return (
    <div className="space-y-4">
      <div className={`${isAhp ? 'bg-[var(--civic-navy-deep)]/60 border-civic-gold/20' : 'bg-gradient-terminal border-border'} border rounded-lg p-4`}>
        <h2 className="text-xl font-bold mb-2" style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: 'var(--civic-cream)' } : { color: 'var(--foreground)' }}>{t('rankings.title')}</h2>

        {/* State Filter with Type-Ahead */}
        <div className="mt-3 mb-3">
          <Popover open={statePopoverOpen} onOpenChange={setStatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={statePopoverOpen}
                className={`w-full justify-between ${isAhp ? 'text-white border-white/20 bg-white/5 hover:bg-white/10 hover:text-white' : ''}`}
              >
                {selectedState === 'USA'
                  ? t('rankings.allHospitalsUsa')
                  : getStateName(selectedState)}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder={t('rankings.typeStateName')} />
                <CommandEmpty>{t('rankings.noStateFound')}</CommandEmpty>
                <CommandGroup className="max-h-[300px] overflow-auto">
                  <CommandItem
                    value="USA"
                    onSelect={() => {
                      onStateChange('USA');
                      onStateSelect('USA');
                      setStatePopoverOpen(false);
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${selectedState === 'USA' ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {t('rankings.allHospitalsUsa')}
                  </CommandItem>
                  {Object.entries(stateAbbreviations).map(([stateName, stateCode]) => (
                    <CommandItem
                      key={stateCode}
                      value={stateName}
                      onSelect={() => {
                        onStateChange(stateCode);
                        onStateSelect(stateCode);
                        setStatePopoverOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${selectedState === stateCode ? 'opacity-100' : 'opacity-0'}`}
                      />
                      {stateName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Breadcrumb */}
        {(selectedState !== 'USA' || selectedCity) && (
          <div className="flex items-center gap-2 text-sm mb-2 flex-wrap">
            <button
              onClick={() => {
                onStateChange('USA');
                onCityChange('');
                onStateSelect('USA');
              }}
              className={`${accentColor} hover:underline`}
            >
              {t('rankings.allHospitals')}
            </button>

            {selectedState !== 'USA' && (
              <>
                <span className={isAhp ? 'text-white/40' : 'text-muted-foreground'}>/</span>
                {selectedCity ? (
                  <button
                    onClick={() => {
                      onCityChange('');
                    }}
                    className={`${accentColor} hover:underline`}
                  >
                    {getStateName(selectedState)}
                  </button>
                ) : (
                  <span className={`font-medium ${isAhp ? 'text-white' : 'text-foreground'}`}>{getStateName(selectedState)}</span>
                )}
              </>
            )}

            {selectedCity && (
              <>
                <span className={isAhp ? 'text-white/40' : 'text-muted-foreground'}>/</span>
                <span className={`font-medium ${isAhp ? 'text-white' : 'text-foreground'}`}>{selectedCity}, {selectedState}</span>
              </>
            )}
          </div>
        )}

        <p className={`text-sm ${isAhp ? 'text-white/70' : 'text-muted-foreground'}`}>
          {t('rankings.showingRanked', { x: Math.min(displayCount, filteredRankings?.length || 0), y: filteredRankings?.length || 0 })}
        </p>
        <p className={`text-xs mt-1 ${isAhp ? 'text-white/60' : ''}`}>
          <Trans
            t={t}
            i18nKey="rankings.legendNote"
            components={{
              red: <span className="text-red-500 font-semibold" />,
              green: <span className="text-green-500 font-semibold" />,
            }}
          />
        </p>
      </div>

      <div ref={scrollContainerRef} className="overflow-auto" style={{ maxHeight: 'calc(100vh - 266px)' }}>
        <table className="w-full text-sm border-collapse">
          <thead className={`sticky top-0 backdrop-blur-sm border-b z-10 ${isAhp ? 'bg-[var(--civic-navy-deep)]/95 border-civic-gold/20' : 'bg-background/95 border-border'}`}>
            <tr>
              <th className={`text-left p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>{t('rankings.headers.rank')}</th>
              <th className={`text-left p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>{t('rankings.headers.hospital')}</th>
              <th className={`text-left p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>{t('rankings.headers.location')}</th>
              <th className={`text-right p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>{t('rankings.headers.avgMarkup')}</th>
              <th className={`text-center p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>{t('rankings.headers.cms')}</th>
              <th className={`text-center p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>{t('rankings.headers.google')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRankings?.slice(0, displayCount).map((ranking: any, index: number) => {
              const meta = hospitalMeta(ranking);
              // Rows arrive in rank order, which is ordered by median-baseline
              // markup — show that metric so the column descends with the rank.
              const rowMarkup = ranking.median_gross_markup_percentage ?? ranking.avg_gross_markup_percentage;
              return (
                <tr
                  key={ranking.hospital_id}
                  onClick={() => onHospitalClick(ranking.hospital_id)}
                  className={`border-b ${isAhp ? 'border-white/10' : 'border-border/30'} cursor-pointer transition-all ${getRowColor(rowMarkup)}`}
                >
                  <td className={`p-2 font-bold text-lg font-mono align-top ${index < 3 ? 'text-red-500' : isAhp ? 'text-white/90' : 'text-white'}`}>#{index + 1}</td>
                  <td className={`p-2 font-medium align-top ${isAhp ? 'text-white' : 'text-foreground'}`}>
                    <div className="leading-tight">{titleCaseHospital(ranking.hospital_name)}</div>
                    {meta && (
                      <div className={`text-[10px] mt-0.5 ${isAhp ? 'text-white/45' : 'text-muted-foreground'}`}>{meta}</div>
                    )}
                  </td>
                  <td className="p-2 text-xs align-top">
                    {!selectedCity && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCityChange(ranking.hospital_city);
                        }}
                        className={`${isAhp ? 'text-civic-gold' : 'text-blue-400'} hover:underline`}
                      >
                        {titleCaseCity(ranking.hospital_city)}, {ranking.hospital_state}
                      </button>
                    )}
                    {selectedCity && (
                      <span className={isAhp ? 'text-white/50' : 'text-muted-foreground'}>
                        {titleCaseCity(ranking.hospital_city)}, {ranking.hospital_state}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right align-top">
                    <span className={`font-bold text-base ${getMarkupColor(rowMarkup)}`}>
                      {rowMarkup != null && rowMarkup >= 0 ? '+' : ''}{rowMarkup?.toFixed(1)}%
                    </span>
                  </td>
                  {/* CMS overall stars */}
                  <td className="p-2 text-center align-top whitespace-nowrap">
                    {ranking.cms_overall_rating != null ? (
                      <span className={`font-semibold ${ratingColor(ranking.cms_overall_rating)}`}>
                        {ranking.cms_overall_rating}<span className="opacity-70">★</span>
                      </span>
                    ) : (
                      <span className={isAhp ? 'text-white/30' : 'text-muted-foreground/50'}>—</span>
                    )}
                  </td>
                  {/* Google rating + review count */}
                  <td className="p-2 text-center align-top whitespace-nowrap">
                    {ranking.google_rating != null ? (
                      <div className="leading-tight">
                        <span className={`font-semibold ${ratingColor(ranking.google_rating)}`}>
                          {Number(ranking.google_rating).toFixed(1)}<span className="opacity-70">★</span>
                        </span>
                        {ranking.google_review_count ? (
                          <div className={`text-[9px] ${isAhp ? 'text-white/40' : 'text-muted-foreground'}`}>
                            {fmtReviews(ranking.google_review_count)}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className={isAhp ? 'text-white/30' : 'text-muted-foreground/50'}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredRankings && displayCount < filteredRankings.length && (
          <div className={`text-center py-4 text-sm ${isAhp ? 'text-white/50' : 'text-muted-foreground'}`}>
            {t('rankings.scrollToLoadMore', { count: filteredRankings.length - displayCount })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HospitalRankingsTable;
